import { Head, useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowRight, Sparkles } from "lucide-react";
import posthog from "posthog-js";
import { useEffect } from "react";

interface Props {
  errors?: Record<string, string | string[]>;
}

export default function Onboarding({ errors }: Props) {
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag("event", "conversion", {
        send_to: "AW-17949750546/UAzMCPD02fgbEJLqje9C",
      });
    }
    if (window.ttq) {
      window.ttq.track("ViewContent", { content_name: "onboarding" });
    }
  }, []);
  const { data, setData, patch, processing } = useForm({
    age: "",
    gender: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch("/onboarding", {
      onSuccess: () => {
        posthog.capture("onboarding_completed", { age: data.age, gender: data.gender });
      },
    });
  };

  const typedErrors = errors as Record<string, string | undefined>;

  return (
    <div className="min-h-screen bg-background">
      <Head title="Complete seu Perfil" />

      <div className="flex min-h-screen flex-col items-center justify-center px-4">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/20 text-primary">
            <Sparkles size={24} />
          </div>
          <span className="text-2xl font-bold text-foreground">fotoCerta</span>
        </div>

        <Card className="w-full max-w-md border-border bg-card backdrop-blur">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Complete seu Perfil</CardTitle>
          </CardHeader>
          <CardContent>
            {(typedErrors?.age || typedErrors?.gender || typedErrors?.base) && (
              <Alert variant="error" className="mb-6">
                <AlertDescription>
                  {typedErrors?.base || typedErrors?.age || typedErrors?.gender}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="age" className="text-foreground">
                  Sua Idade
                </Label>
                <Input
                  id="age"
                  type="number"
                  min={18}
                  max={120}
                  value={data.age}
                  onChange={(e) => setData("age", e.target.value)}
                  placeholder="Digite sua idade"
                  className="border-input bg-background text-foreground placeholder:text-muted-foreground"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="gender" className="text-foreground">
                  Seu Gênero
                </Label>
                <Select
                  value={data.gender}
                  onValueChange={(value) => setData("gender", value)}
                  required
                >
                  <SelectTrigger className="border-input bg-background text-foreground">
                    <SelectValue placeholder="Selecione seu gênero" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-card">
                    <SelectItem value="male">Masculino</SelectItem>
                    <SelectItem value="female">Feminino</SelectItem>
                    <SelectItem value="other">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                disabled={processing || !data.age || !data.gender}
                className="w-full gap-2"
              >
                {processing ? "Salvando..." : "Continuar"}
                <ArrowRight size={16} />
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 max-w-sm text-center text-sm text-muted-foreground">
          Essas informações nos ajudam a encontrar as fotos certas para você e garantem que você
          veja conteúdo relevante.
        </p>
      </div>
    </div>
  );
}
